import React, { useEffect, useRef, useState } from "react";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

type TextExperienceData = {
  text: {
    title?: string;
    description?: string;
    created_at?: string;
  };
};

type Props = {
  data: TextExperienceData;
};

export const TaasExpText: React.FC<Props> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isDescriptionTruncated, setIsDescriptionTruncated] = useState(false);
  const descriptionRef = useRef<HTMLDivElement | null>(null);

  const stripHtmlTags = (html: string) => {
    if (!html) return "";
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const isRichTextHtml = (value: string) => /<\/?[a-z][\s\S]*>/i.test(value);

  const title = data.text?.title;
  const descriptionRaw = data.text?.description?.trim() || "";
  const isRichDescription = Boolean(descriptionRaw && isRichTextHtml(descriptionRaw));
  const descriptionPlain = descriptionRaw ? stripHtmlTags(descriptionRaw) : "";
  const description = descriptionPlain || undefined;
  const createdAt = data.text?.created_at;
  const formattedCreatedAt = React.useMemo(() => {
    if (!createdAt) return "";

    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return createdAt;

    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  }, [createdAt]);

  useEffect(() => {
    setShowFullDescription(false);
  }, [descriptionRaw]);

  useEffect(() => {
    if (showFullDescription) return;

    const descriptionElement = descriptionRef.current;
    if (!descriptionElement) {
      setIsDescriptionTruncated(false);
      return;
    }

    const measureTarget =
      descriptionElement.querySelector<HTMLElement>(".ql-editor") || descriptionElement;

    const updateTruncation = () => {
      setIsDescriptionTruncated(measureTarget.scrollHeight > measureTarget.clientHeight + 1);
    };

    updateTruncation();

    const resizeObserver = new ResizeObserver(updateTruncation);
    resizeObserver.observe(measureTarget);

    return () => resizeObserver.disconnect();
  }, [descriptionRaw, isMobile, showFullDescription, isRichDescription]);

  const descriptionTypographySx = {
    fontFamily: "Arial, sans-serif",
    color: "#554f61",
    fontSize: { xs: "1.12rem", sm: "1.15rem", md: "1.3rem" },
    wordBreak: "break-word" as const,
    lineHeight: isMobile ? 1.6 : 1.65,
  };

  // const clampSx = showFullDescription
  //   ? {}
  //   : {
  //       display: "-webkit-box",
  //       WebkitLineClamp: 10,
  //       WebkitBoxOrient: "vertical" as const,
  //       overflow: "hidden",
  //     };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "auto",
        backgroundColor: "#f8f8f8",
        display: "flex",
        flexDirection: "column",
        p: { xs: "52px 22px 24px 22px", sm: "52px 22px 24px 22px" },
      }}
    >
      {formattedCreatedAt && (
        <Typography
          component="time"
          dateTime={createdAt}
          sx={{
            color: "#6d35dc",
            fontFamily: "'Segoe Print', 'Bradley Hand', cursive",
            fontSize: { xs: "1rem", sm: "1.08rem" },
            lineHeight: 1.35,
            mb: { xs: 1.2, sm: 1.35 },
          }}
        >
          {formattedCreatedAt}
        </Typography>
      )}

      {title && (
        <>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.72rem", sm: "1.95rem" },
              letterSpacing: "-0.025em",
              lineHeight: 1.18,
              color: "var(--neutralBlack)",
              maxWidth: "18ch",
              textShadow: "none",
            }}
          >
            {title}
          </Typography>
          <Box
            aria-hidden
            sx={{
              width: 38,
              height: 2,
              mt: { xs: 2.6, sm: 2.85 },
              mb: { xs: 2.25, sm: 2.75 },
              background: "linear-gradient(90deg, #7137df 0%, rgba(113, 55, 223, 0) 100%)",
            }}
          />
        </>
      )}

      {description && (
        <Box>
          {isRichDescription ? (
            <Box
              ref={descriptionRef}
              sx={{
                ...descriptionTypographySx,
                "& .quill": { bgcolor: "transparent" },
                "& .ql-toolbar": { display: "none" },
                "& .ql-container.ql-snow": {
                  border: "none",
                  fontFamily: "inherit",
                  fontSize: "inherit",
                  color: "inherit",
                },
                "& .ql-editor": {
                  padding: 0,
                  lineHeight: "inherit",
                  // ...clampSx,
                  "& p": { marginBottom: "0.55em" },
                  "& p:last-child": { marginBottom: 0 },
                  "&.ql-blank::before": { display: "none" },
                },
              }}
            >
              <ReactQuill
                theme="snow"
                value={descriptionRaw}
                readOnly
                modules={{ toolbar: false }}
              />
            </Box>
          ) : (
            <Typography
              ref={descriptionRef}
              component="div"
              sx={{
                ...descriptionTypographySx,
                whiteSpace: "pre-wrap",
                // ...clampSx,
              }}
            >
              {description}
            </Typography>
          )}
          {isDescriptionTruncated && (
            <Box
              component="button"
              type="button"
              aria-expanded={showFullDescription}
              aria-label={showFullDescription ? "See less" : "See more"}
              onClick={(event) => {
                event.stopPropagation();
                setShowFullDescription((prev) => !prev);
              }}
              sx={{
                mt: { xs: 1, sm: 1.15 },
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.45,
                appearance: "none",
                border: "1px solid rgba(26, 21, 40, 0.16)",
                backgroundColor: "rgba(26, 21, 40, 0.06)",
                color: "#1a1528",
                px: 1.35,
                py: 0.5,
                minHeight: 32,
                borderRadius: "999px",
                cursor: "pointer",
                fontFamily: "Arial, sans-serif",
                fontSize: "0.68rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                boxShadow: "0 4px 12px rgba(26,21,40,0.08)",
                transition: "transform 120ms ease, background 120ms ease",
                WebkitTapHighlightColor: "transparent",
                "&:hover": {
                  backgroundColor: "rgba(26, 21, 40, 0.1)",
                },
                "&:active": { transform: "scale(0.94)" },
              }}
            >
              {showFullDescription ? (
                <ExpandLessRoundedIcon sx={{ fontSize: 18 }} />
              ) : (
                <ExpandMoreRoundedIcon sx={{ fontSize: 18 }} />
              )}
              <Box component="span">{showFullDescription ? "See less" : "See more"}</Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
